import { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import { Button, Card, Grid } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'

import {
  EOrderDateType,
  EOrderStatus,
  IOrder,
  OrderSummary,
} from '../../../../types/order'
import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import { ContentWrapper } from '../../../../components/ContentWrapper'
import useAxios, { axiosInstance } from '../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../types/pagination'
import TrackingTable from '../../components/TrackingTable'
import PageUrls from '../../../../constants/page-urls'
import TrackingFilter, { IFilterMapped } from '../../components/TrackingFilter'
import { CreateTrackFormModal } from '../../components/CreateTrackFormModal'
import { CancelTrackFormModal } from '../../components/CancelTrackFormModal'
import { PrintTrackFormModal } from '../../components/PrintTrackFormModal'
import { IUser, UserRole } from '../../../../types/user'
import { useUser } from '../../../../hooks/useUser'
import { FiPrinter, FiRefreshCcw, FiTrash2 } from 'react-icons/fi'
import dayjs from 'dayjs'
import { message } from 'antd'
import ConfirmModal from '../../../../components/ConfirmModal'
import { AbilityContext } from '../../../../components/Ability'
import { isAllowNewFeature } from '../../../../constants/user'

const Menus: any[] = []

const TrackingListPage = () => {
  const ability = useContext(AbilityContext)
  const navigate = useNavigate()
  const queryStr = queryString.parse(window.location.search)
  const { user, merchant } = useUser()

  const [isConfirmModal, setIsConfirmModal] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [cacheBuster, setCacheBuster] = useState<number | undefined>(undefined)

  const query: IFilterMapped = {
    page: Number(queryStr.page ?? 1),
    perPage: 15,
    status: EOrderStatus.PENDING,
    trackingProvider: (queryStr.trackingProvider as any) ?? '',
    colors: (queryStr.colors as any) ?? undefined,
    sizes: (queryStr.sizes as any) ?? undefined,
    overdue: (queryStr.overdue as any) ?? undefined,
    dateType: (queryStr.dateType as any) ?? EOrderDateType.CREATED,
    startDate: queryStr?.startDate
      ? (queryStr?.startDate as any)
      : dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
    endDate: queryStr?.endDate
      ? (queryStr?.endDate as any)
      : dayjs().endOf('month').format('YYYY-MM-DD'),
  }

  const [searchFilter, setSearchFilter] = useState<IFilterMapped>(query)
  const [filter, setFilter] = useState<IFilterMapped>(query)

  const [selectedOrders, setSelectedOrders] = useState<IOrder[]>([])
  const [pendingRemovedIds, setPendingRemovedIds] = useState<Set<string>>(
    new Set(),
  )
  // const hasTrackingCodeOrder = selectedOrders.filter(order => order.trackingCode)
  const onFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      setFilter(newFilter)
    },
    [setFilter],
  )

  const handleSearch = (newFilter: IFilterMapped) => {
    const filter = { ...newFilter, page: 1 }
    setFilter(filter)
    setSearchFilter(filter)
    setSelectedOrders([])
    setPendingRemovedIds(new Set())
    const newQuery = queryString.stringify({ ...filter })
    navigate(`${PageUrls.Tracking}?${newQuery}`, {
      replace: true,
    })
  }

  const handlePageChange = (newFilter: IFilterMapped) => {
    const filter = { ...newFilter }
    setFilter(filter)
    setSearchFilter(filter)
    setPendingRemovedIds(new Set())
    const newQuery = queryString.stringify({ ...filter })
    navigate(`${PageUrls.Tracking}?${newQuery}`, {
      replace: true,
    })
  }

  useEffect(() => {
    handleSearch(searchFilter)
  }, [])

  const [isModalCreateTrack, setIsModalCreateTrack] = useState<boolean>(false)
  const [isModalCancelTrack, setIsModalCancelTrack] = useState<boolean>(false)
  const [isModalPrintTrack, setIsModalPrintTrack] = useState<boolean>(false)

  const hasTrackingCodeOrder = selectedOrders.filter(
    (order) => order.trackingCode,
  )

  const hasTrackingCodeOrderCancelable = hasTrackingCodeOrder.filter(
    (order) => order.status === EOrderStatus.TRACKING,
  )

  const trackingProvider =
    (searchFilter?.trackingProvider as any) == 'all'
      ? undefined
      : searchFilter.trackingProvider
  let status =
    (searchFilter?.status as any) == 'all' ? undefined : searchFilter.status
  if (trackingProvider) {
    status = undefined
  }
  const ordersList = useAxios<
    PaginationResponseInterface<IOrder> & { total: OrderSummary }
  >(
    {
      method: 'GET',
      url: '/order',
      params: {
        ...searchFilter,
        showTotal: '1',
        dateType:
          (searchFilter?.dateType as any) == 'all'
            ? undefined
            : searchFilter.dateType,
        statusShowCrmTelesale:
          (searchFilter?.statusShowCrmTelesale as any) == 'all'
            ? undefined
            : searchFilter.statusShowCrmTelesale,
        status: status !== 'all' ? status : undefined,
        statusOrderShowTracking:
          (searchFilter?.statusOrderShowTracking as any) == 'all'
            ? undefined
            : searchFilter.statusOrderShowTracking,
        paymentMethod:
          (searchFilter?.paymentMethod as any) == 'all'
            ? undefined
            : searchFilter.paymentMethod,
        trackingProvider,
        exceptStatus: trackingProvider ? [EOrderStatus.CANCLELLED] : undefined,
        providerStatus:
          searchFilter?.providerStatus === 'all'
            ? undefined
            : searchFilter?.providerStatus,
        _cb: cacheBuster,
      },
    },
    {
      revalidateOnFocus: false,
    },
  )

  const visibleRecords = (ordersList.data?.records ?? []).filter(
    (o) => !pendingRemovedIds.has(o.objectId),
  )

  // auto-clear ids ที่ backend confirm ว่าหายแล้ว (ไม่อยู่ใน records ที่ fetch กลับมา)
  useEffect(() => {
    if (pendingRemovedIds.size === 0 || !ordersList.data) return
    const stillPresent = new Set(
      ordersList.data.records.map((o) => o.objectId),
    )
    let changed = false
    const next = new Set<string>()
    pendingRemovedIds.forEach((id) => {
      if (stillPresent.has(id)) {
        next.add(id)
      } else {
        changed = true
      }
    })
    if (changed) setPendingRemovedIds(next)
  }, [ordersList.data])

  const onUpdateTrackingSuccess = useCallback(
    async (successOrderIds: string[] = []) => {
      if (successOrderIds.length > 0) {
        setSelectedOrders((prev) =>
          prev.filter((o) => !successOrderIds.includes(o.objectId)),
        )
        setPendingRemovedIds((prev) => {
          const next = new Set(prev)
          successOrderIds.forEach((id) => next.add(id))
          return next
        })
        await ordersList.mutate(
          (current) =>
            current && {
              ...current,
              data: {
                ...current.data,
                records: current.data.records.filter(
                  (o: IOrder) => !successOrderIds.includes(o.objectId),
                ),
                count: Math.max(
                  0,
                  (current.data.count ?? 0) - successOrderIds.length,
                ),
              },
            },
          false,
        )
      } else {
        setSelectedOrders([])
      }
      // bump cache-buster เพื่อให้ swrKey + Redis cache key เปลี่ยน ดึงข้อมูลสดจาก DB
      setCacheBuster(Date.now())
    },
    [ordersList],
  )

  const onUpdateTrackingStatus = useCallback(async () => {
    setIsLoading(true)
    setIsConfirmModal(false)
    if (!filter.trackingProvider) {
      message.error(
        'กรุณาค้นหาคำสั่งซื้อจาก ช่องทางการขนส่ง ก่อนทำการอัพเดตสถานะพัสดุ',
      )
      setIsLoading(false)
      return
    }

    if (hasTrackingCodeOrder.length === 0) {
      message.error('กรุณาเลือกออเดอร์')
      setIsLoading(false)
      return
    }

    if (hasTrackingCodeOrder.length > 30) {
      message.error('กรุณาเลือกออเดอร์ไม่เกิน 30 รายการ')
      setIsLoading(false)
      return
    }

    try {
      await axiosInstance.post(
        `/trackings/${filter.trackingProvider.toLowerCase()}/status`,
        {
          orders: selectedOrders.map((order) => order.objectId),
        },
      )
      setSelectedOrders([])
      await ordersList.mutate()
      message.success('อัพเดตสถานะพัสดุสำเร็จ')
    } catch (error) {
      const statusCode = (error as any)?.response?.status ?? 500
      if (statusCode === 404) {
        message.error(
          'ผู้ให้บริการพัสดุยังไม่เปิดให้บริการ กรุณาเลือกผู้ให้บริการพัสดุอื่น',
        )
        setIsLoading(false)
        return
      }
      message.error('อัพเดตสถานะพัสดุไม่สำเร็จ')
    } finally {
      setIsLoading(false)
    }
  }, [filter.trackingProvider, selectedOrders])

  return (
    <ContentWrapper>
      <Helmet title="สร้างหมายเลขพัสดุ / พิมพ์ใบลาเบล" />
      <ConfirmModal
        open={isConfirmModal}
        onClose={() => setIsConfirmModal(false)}
        onConfirm={onUpdateTrackingStatus}
        title={'ยืนยันการอัพเดตสถานะพัสดุ'}
        message={'คุณต้องการอัพเดตสถานะพัสดุหรือไม่?'}
      />
      <Grid container justifyContent="flex-start" alignItems="center">
        <Grid item>
          <HeaderContent
            goBack
            title="สร้างหมายเลขพัสดุ / พิมพ์ใบลาเบล"
            subTitle={`วันที่ ${dayjs(filter.startDate).format(
              'DD/MM/YYYY',
            )} - ${dayjs(filter.endDate).format('DD/MM/YYYY')}`}
            routes={Menus}
            descriptions="สร้างหมายเลขพัสดุ"
            tags={
              [UserRole.ADMIN, UserRole.MERCHANT].some(
                (role) => (user.roles as UserRole[])?.includes(role),
              ) || merchant.config?.display?.totalOrder
                ? [
                  {
                    title: 'จำนวน',
                    color: 'error',
                    value: ordersList.data?.count ?? 0,
                    classifier: 'ออเดอร์',
                  },
                ]
                : undefined
            }
          />
        </Grid>
      </Grid>
      <Card
        sx={{
          padding: 2,
        }}
      >
        <TrackingFilter
          filter={filter}
          selectedOrders={selectedOrders}
          setSelectedOrders={setSelectedOrders}
          onFilterChange={onFilterChange}
          onSearch={handleSearch}
        />

        <Grid container spacing={1} mb={2}>
          <CreateTrackFormModal
            isOpen={isModalCreateTrack}
            onClose={(visible) => {
              setIsModalCreateTrack(visible)
            }}
            orders={selectedOrders.filter((order) => !order.trackingCode)}
            onUpdateTrackingSuccess={onUpdateTrackingSuccess}
          />
          <CancelTrackFormModal
            trackingProvider={filter?.trackingProvider!}
            isOpen={isModalCancelTrack}
            onClose={async (visible) => {
              setIsModalCancelTrack(visible)
              await onUpdateTrackingSuccess()
            }}
            orders={selectedOrders.filter((order) => order.trackingCode)}
            onUpdateTrackingSuccess={onUpdateTrackingSuccess}
          />

          {hasTrackingCodeOrder?.length < selectedOrders.length && (
            <>
              <Grid item>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<SendIcon />}
                  color={'success'}
                  onClick={() => setIsModalCreateTrack(true)}
                >
                  สร้างหมายเลข Tracking{' '}
                  {`(${selectedOrders.length - hasTrackingCodeOrder?.length})`}
                </Button>
              </Grid>
            </>
          )}
          {hasTrackingCodeOrder.length > 0 &&
            filter.trackingProvider?.length! > 0 && (
              <>
                <PrintTrackFormModal
                  trackingProvider={filter?.trackingProvider}
                  isOpen={isModalPrintTrack}
                  onClose={(visible) => {
                    setIsModalPrintTrack(visible)
                    setSelectedOrders([])
                  }}
                  orders={selectedOrders.filter((order) => order.trackingCode)}
                  onUpdateTrackingSuccess={onUpdateTrackingSuccess}
                />
                <Grid item>
                  <Button
                    variant="contained"
                    size="medium"
                    disabled={ordersList.data?.records[0].trackingProvider !== filter.trackingProvider}
                    startIcon={<FiPrinter />}
                    color={'success'}
                    onClick={() => setIsModalPrintTrack(true)}
                  >
                    พิมพ์ใบปะหน้า {`(${hasTrackingCodeOrder.filter((order) => order.status === EOrderStatus.TRACKING).length})
                    ${ordersList.data?.records[0].trackingProvider !== filter.trackingProvider ? ' กรุณาเลือกช่องทางขนส่งให้ตรงกับคำสั่งซื้อ' : ''}
                    `}
                  </Button>
                </Grid>
                {filter.trackingProvider &&
                  isAllowNewFeature(user as IUser) && (
                    <Grid item>
                      <Button
                        variant="contained"
                        size="medium"
                        startIcon={<FiRefreshCcw />}
                        color={'warning'}
                        onClick={() => setIsConfirmModal(true)}
                      >
                        อัพเดตสถานะพัสดุ {`(${hasTrackingCodeOrder.length})`}
                      </Button>
                    </Grid>
                  )}
                {hasTrackingCodeOrderCancelable.length > 0 && (
                  <>
                    <Grid item>
                      <Button
                        variant="contained"
                        size="medium"
                        startIcon={<FiTrash2 />}
                        color={'error'}
                        onClick={() => setIsModalCancelTrack(true)}
                      >
                        ยกเลิกเลขพัสดุ{' '}
                        {`(${hasTrackingCodeOrderCancelable.length})`}
                      </Button>
                    </Grid>
                  </>
                )}
              </>
            )}
        </Grid>

        <TrackingTable
          isLoading={ordersList.isLoading || isLoading}
          data={visibleRecords}
          selectedData={selectedOrders}
          setSelectedData={setSelectedOrders}
          filter={{ ...filter }}
          onFilterChange={handleSearch}
          handlePageChange={handlePageChange}
          onRefresh={() => handleSearch(searchFilter)}
          total={ordersList.data?.count ?? 0}
        />
      </Card>
    </ContentWrapper>
  )
}

export default TrackingListPage
